/**
 * DesignSystem — 西蒙猫设计系统展示页
 * 暗夜萤火 · 组件规范
 *
 * 展示内容: 色彩规范 / 设计Token / 公共组件 / 图标库 / 布局规范
 */

import React from 'react';
import {
  Typography, Card, Table, Tag, Space, Divider,
  Button, Row, Col, Descriptions,
} from 'antd';
import {
  // 展示用图标 — 30+
  HomeOutlined, AppstoreOutlined, BarChartOutlined, SettingOutlined,
  SearchOutlined, BellOutlined, UserOutlined, TeamOutlined,
  HeartOutlined, StarOutlined, FireOutlined, ThunderboltOutlined,
  CloudOutlined, DatabaseOutlined, SafetyOutlined, LockOutlined,
  UnlockOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
  PlusOutlined, MinusOutlined, CheckOutlined, CloseOutlined,
  InfoCircleOutlined, ExclamationCircleOutlined, QuestionCircleOutlined,
  DownloadOutlined, UploadOutlined, CopyOutlined, ShareAltOutlined,
  CalendarOutlined, MessageOutlined, MailOutlined, PhoneOutlined,
  ScanOutlined, QrcodeOutlined, WifiOutlined, GlobalOutlined,
  LayoutOutlined, RobotOutlined, AimOutlined, PlayCircleOutlined,
  ApartmentOutlined, BookOutlined, ShopOutlined,
} from '@ant-design/icons';
import StatCard from '../../components/StatCard';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import KnowledgeCard from '../../components/KnowledgeCard';

const { Title, Paragraph, Text } = Typography;

/* ─────────────────────────────────────────────
   色彩规范数据
   ───────────────────────────────────────────── */
const colorGroups = [
  {
    title: '夜色基础 — 6级分层',
    desc: '70% 深蓝夜色 · 主背景层，由深至浅',
    colors: [
      { name: '--night-950', value: '#030B18', label: '极深' },
      { name: '--night-900 / --night', value: '#061226', label: '主背景' },
      { name: '--night-850', value: '#08182F', label: '次深' },
      { name: '--night-800', value: '#0B1E39', label: '中层' },
      { name: '--night-700 / --moon-blue', value: '#10294A', label: '月光蓝' },
      { name: '--moon-blue-light', value: '#1E4478', label: '月光浅' },
    ],
  },
  {
    title: '文字层级 — 4级',
    desc: 'Primary → Muted，透明度递减',
    colors: [
      { name: '--text-primary', value: '#F3F6FA', label: '主文字' },
      { name: '--text-secondary', value: 'rgba(230,238,248,0.68)', label: '次文字' },
      { name: '--text-tertiary', value: 'rgba(230,238,248,0.42)', label: '三级文字' },
      { name: '--text-muted', value: 'rgba(230,238,248,0.28)', label: '弱化' },
    ],
  },
  {
    title: '强调色 — 萤火 · 月光',
    desc: '核心强调色仅占 2%，克制使用',
    colors: [
      { name: '--accent', value: '#E8D28A', label: '萤火金' },
      { name: '--accent-secondary', value: '#A8C7D8', label: '湖滨蓝' },
      { name: '--firefly', value: '#F4DFA2', label: '萤光' },
      { name: '--deer-glow', value: '#D9EEFF', label: '神鹿高光' },
    ],
  },
  {
    title: 'Surface — 半透明玻璃',
    desc: '玻璃态层级，由透明至实色',
    colors: [
      { name: '--surface-1', value: 'rgba(12,30,54,0.72)', label: '玻璃1' },
      { name: '--surface-2', value: 'rgba(15,37,65,0.68)', label: '玻璃2' },
      { name: '--surface-3', value: 'rgba(20,45,76,0.62)', label: '玻璃3' },
    ],
  },
  {
    title: '边框',
    desc: '三层边框由弱至强',
    colors: [
      { name: '--border-subtle', value: 'rgba(255,255,255,0.04)', label: '极弱' },
      { name: '--border-default', value: 'rgba(255,255,255,0.08)', label: '默认' },
      { name: '--border-hover', value: 'rgba(255,255,255,0.16)', label: '悬停' },
    ],
  },
  {
    title: '玻璃材质',
    desc: '极少使用，强调精致感',
    colors: [
      { name: '--glass-sm', value: 'rgba(255,255,255,0.03)', label: '微玻璃' },
      { name: '--glass-md', value: 'rgba(255,255,255,0.06)', label: '中玻璃' },
      { name: '--glass-lg', value: 'rgba(255,255,255,0.10)', label: '强玻璃' },
    ],
  },
];

/* ─────────────────────────────────────────────
   设计Token数据
   ───────────────────────────────────────────── */
const tokenData = {
  radius: [
    { name: '--radius-sm', value: '12px', usage: '小元素: Tag、Badge' },
    { name: '--radius-md', value: '18px', usage: '卡片、面板 (默认)' },
    { name: '--radius-lg', value: '24px', usage: '大容器、模态框' },
    { name: '--radius-pill', value: '999px', usage: '按钮胶囊态' },
  ],
  shadow: [
    { name: '--shadow-sm', value: '0 2px 8px rgba(0,0,0,0.15)', usage: '悬浮小元素' },
    { name: '--shadow-md', value: '0 8px 32px rgba(0,0,0,0.20)', usage: '卡片、下拉' },
    { name: '--shadow-lg', value: '0 20px 60px rgba(0,0,0,0.18)', usage: '模态框、抽屉' },
    { name: '--shadow-glow', value: '0 0 24px rgba(232,210,138,0.06)', usage: '萤火微光' },
  ],
  spacing: [
    { name: '--space-xs', value: '4px', usage: '图标间距' },
    { name: '--space-sm', value: '8px', usage: '紧凑间距' },
    { name: '--space-md', value: '16px', usage: '标准间距' },
    { name: '--space-lg', value: '24px', usage: '区块间距' },
    { name: '--space-xl', value: '32px', usage: '大区块分隔' },
  ],
  font: [
    { name: '--font-sans', value: 'Inter, Noto Sans SC, …', usage: '默认正文' },
    { name: '--font-serif', value: 'Noto Serif SC, …', usage: '标题装饰' },
    { name: '--font-mono', value: 'SF Mono, JetBrains Mono, …', usage: '代码' },
    { name: '--font-display', value: 'Cormorant Garamond, …', usage: '展示字体' },
  ],
  animation: [
    { name: '--ease', value: 'cubic-bezier(0.22,1,0.36,1)', usage: '标准缓动' },
    { name: '--ease-spring', value: 'cubic-bezier(0.34,1.56,0.64,1)', usage: '弹性缓动' },
    { name: '--duration-fast', value: '200ms', usage: '快速交互' },
    { name: '--duration-normal', value: '400ms', usage: '标准过渡' },
    { name: '--duration-slow', value: '700ms', usage: '慢速入场' },
  ],
};

const tokenColumns = [
  { title: 'Token', dataIndex: 'name', key: 'name', render: (v) => <code style={{ color: '#E8D28A', fontSize: 12 }}>{v}</code> },
  { title: '值', dataIndex: 'value', key: 'value', render: (v) => <code style={{ color: '#A8C7D8', fontSize: 12 }}>{v}</code> },
  { title: '用途', dataIndex: 'usage', key: 'usage' },
];

/* ─────────────────────────────────────────────
   组件Props表格数据
   ───────────────────────────────────────────── */
const statCardProps = [
  { prop: 'title', type: 'string', default: '—', desc: '统计项标题' },
  { prop: 'value', type: 'any', default: '—', desc: '统计值（传给 Statistic）' },
  { prop: 'suffix', type: 'string', default: '—', desc: '值后缀（如 "个" / "%"）' },
  { prop: 'icon', type: 'node', default: '—', desc: '前缀图标（React 节点）' },
  { prop: 'color', type: 'string', default: "'#F4DFA2'", desc: '强调色（CSS变量名或色值）' },
];

const emptyStateProps = [
  { prop: 'description', type: 'node | string', default: "'暂无数据'", desc: '描述文案' },
  { prop: 'action', type: 'node', default: '—', desc: '底部操作按钮区域' },
  { prop: 'image', type: 'string', default: '内置✨', desc: '自定义图片URL' },
];

const pageHeaderProps = [
  { prop: 'title', type: 'string', default: '—', desc: '页面标题' },
  { prop: 'description', type: 'node | string', default: '—', desc: '页面描述（副标题）' },
  { prop: 'actions', type: 'node', default: '—', desc: '右侧操作区域（React 节点）' },
];

const knowledgeCardProps = [
  { prop: 'document', type: 'Object', default: '—', desc: '知识文档对象' },
  { prop: 'onEdit', type: '(doc) => void', default: '—', desc: '编辑回调' },
  { prop: 'onDelete', type: '(id) => void', default: '—', desc: '删除回调' },
];

const propColumns = [
  { title: 'Prop', dataIndex: 'prop', key: 'prop', render: (v) => <code style={{ color: '#6366f1', fontSize: 12 }}>{v}</code> },
  { title: '类型', dataIndex: 'type', key: 'type', render: (v) => <Tag color="blue" style={{ fontSize: 11, border: 'none' }}>{v}</Tag> },
  { title: '默认值', dataIndex: 'default', key: 'default', render: (v) => <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{v}</span> },
  { title: '说明', dataIndex: 'desc', key: 'desc' },
];

/* ─────────────────────────────────────────────
   KnowledgeCard mock数据
   ───────────────────────────────────────────── */
const mockDoc = {
  id: 'kb-001',
  title: '美国市场 Prime Day 运营策略全指南',
  type: 'ad_strategy',
  country: 'US',
  summary: '覆盖 Prime Day 大促前的选品、定价、广告投放节奏，包含 ACOS 目标区间与预算分配建议。',
  updatedAt: '2025-07-12',
  agentRefs: 128,
};

/* ─────────────────────────────────────────────
   图标库数据 — 36个
   ───────────────────────────────────────────── */
const iconList = [
  { icon: <HomeOutlined />, name: 'HomeOutlined' },
  { icon: <AppstoreOutlined />, name: 'AppstoreOutlined' },
  { icon: <BarChartOutlined />, name: 'BarChartOutlined' },
  { icon: <SettingOutlined />, name: 'SettingOutlined' },
  { icon: <SearchOutlined />, name: 'SearchOutlined' },
  { icon: <BellOutlined />, name: 'BellOutlined' },
  { icon: <UserOutlined />, name: 'UserOutlined' },
  { icon: <TeamOutlined />, name: 'TeamOutlined' },
  { icon: <HeartOutlined />, name: 'HeartOutlined' },
  { icon: <StarOutlined />, name: 'StarOutlined' },
  { icon: <FireOutlined />, name: 'FireOutlined' },
  { icon: <ThunderboltOutlined />, name: 'ThunderboltOutlined' },
  { icon: <CloudOutlined />, name: 'CloudOutlined' },
  { icon: <DatabaseOutlined />, name: 'DatabaseOutlined' },
  { icon: <SafetyOutlined />, name: 'SafetyOutlined' },
  { icon: <LockOutlined />, name: 'LockOutlined' },
  { icon: <UnlockOutlined />, name: 'UnlockOutlined' },
  { icon: <EyeOutlined />, name: 'EyeOutlined' },
  { icon: <EditOutlined />, name: 'EditOutlined' },
  { icon: <DeleteOutlined />, name: 'DeleteOutlined' },
  { icon: <PlusOutlined />, name: 'PlusOutlined' },
  { icon: <CheckOutlined />, name: 'CheckOutlined' },
  { icon: <CloseOutlined />, name: 'CloseOutlined' },
  { icon: <InfoCircleOutlined />, name: 'InfoCircleOutlined' },
  { icon: <ExclamationCircleOutlined />, name: 'ExclamationCircleOutlined' },
  { icon: <DownloadOutlined />, name: 'DownloadOutlined' },
  { icon: <UploadOutlined />, name: 'UploadOutlined' },
  { icon: <CopyOutlined />, name: 'CopyOutlined' },
  { icon: <ShareAltOutlined />, name: 'ShareAltOutlined' },
  { icon: <CalendarOutlined />, name: 'CalendarOutlined' },
  { icon: <MessageOutlined />, name: 'MessageOutlined' },
  { icon: <MailOutlined />, name: 'MailOutlined' },
  { icon: <GlobalOutlined />, name: 'GlobalOutlined' },
  { icon: <LayoutOutlined />, name: 'LayoutOutlined' },
  { icon: <RobotOutlined />, name: 'RobotOutlined' },
  { icon: <PlayCircleOutlined />, name: 'PlayCircleOutlined' },
];

/* ─────────────────────────────────────────────
   布局侧边栏宽度/高度常量
   ───────────────────────────────────────────── */
const SECTION_GAP = 56;
const DIVIDER_STYLE = { borderColor: 'var(--border-subtle)', margin: '40px 0 32px' };

/* ─────────────────────────────────────────────
   主组件
   ───────────────────────────────────────────── */
const DesignSystem = () => {
  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 24px 80px' }}>

      {/* ════════════════════════════════════════
          0. 顶部标题区
          ════════════════════════════════════════ */}
      <div style={{ marginBottom: SECTION_GAP }}>
        <Title level={1} style={{
          fontSize: 36, fontWeight: 700,
          color: 'var(--text-primary)',
          margin: 0, marginBottom: 8,
          letterSpacing: '-0.03em',
          fontFamily: 'var(--font-display)',
        }}>
          <span style={{
            background: 'linear-gradient(135deg, #F4DFA2 0%, #E8D4A0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>西蒙猫设计系统</span>
        </Title>
        <Space size={12} align="center">
          <Text style={{ fontSize: 18, color: 'var(--text-secondary)', fontFamily: 'var(--font-serif)' }}>
            暗夜萤火 · 组件规范
          </Text>
          <Tag color="warning" style={{ border: 'none', fontSize: 11, borderRadius: 6 }}>
            v2.0
          </Tag>
        </Space>
        <Paragraph style={{ fontSize: 14, color: 'var(--text-tertiary)', marginTop: 20, maxWidth: 640, lineHeight: 1.8 }}>
          基于萤火秘境设计语言构建的内部组件规范。色彩灵感来源于夜色森林中流萤的暖金光点，
          克制、温润、层次分明。本页汇总项目中所有公共组件、设计Token、图标资源与布局模式。
        </Paragraph>
      </div>

      {/* ════════════════════════════════════════
          1. 色彩规范
          ════════════════════════════════════════ */}
      <Divider style={DIVIDER_STYLE}>
        <span style={{ fontSize: 15, color: 'var(--text-secondary)', fontFamily: 'var(--font-sans)' }}>
          01 · 色彩规范
        </span>
      </Divider>

      {colorGroups.map((group) => (
        <div key={group.title} style={{ marginBottom: 36 }}>
          <Title level={4} style={{ fontSize: 16, color: 'var(--text-primary)', marginBottom: 4, fontWeight: 600 }}>
            {group.title}
          </Title>
          <Text style={{ fontSize: 13, color: 'var(--text-tertiary)', display: 'block', marginBottom: 16 }}>
            {group.desc}
          </Text>
          <Row gutter={[12, 12]}>
            {group.colors.map((c) => (
              <Col key={c.name} xs={12} sm={8} md={6} lg={4}>
                <Card
                  bordered={false}
                  style={{
                    background: 'var(--glass-sm)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-sm)',
                    overflow: 'hidden',
                    transition: 'border-color 200ms var(--ease)',
                    cursor: 'default',
                  }}
                  styles={{ body: { padding: 0 } }}
                  hoverable
                >
                  {/* 色块预览 */}
                  <div style={{
                    width: '100%',
                    height: 64,
                    background: c.value,
                    borderBottom: '1px solid var(--border-subtle)',
                  }} />
                  <div style={{ padding: '10px 14px' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500, marginBottom: 2 }}>
                      {c.label}
                    </div>
                    <code style={{ fontSize: 11, color: '#A8C7D8', display: 'block' }}>{c.name}</code>
                    <code style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{c.value}</code>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      ))}

      {/* ════════════════════════════════════════
          2. 设计Token
          ════════════════════════════════════════ */}
      <Divider style={DIVIDER_STYLE}>
        <span style={{ fontSize: 15, color: 'var(--text-secondary)' }}>
          02 · 设计Token
        </span>
      </Divider>

      {Object.entries(tokenData).map(([key, data]) => (
        <div key={key} style={{ marginBottom: 32 }}>
          <Title level={4} style={{ fontSize: 16, color: 'var(--text-primary)', marginBottom: 12, fontWeight: 600 }}>
            {key === 'radius' && '圆角 Radius'}
            {key === 'shadow' && '阴影 Shadow'}
            {key === 'spacing' && '间距 Spacing'}
            {key === 'font' && '字体 Font'}
            {key === 'animation' && '动画 Animation'}
          </Title>
          <Table
            columns={tokenColumns}
            dataSource={data.map((row, i) => ({ ...row, key: i }))}
            pagination={false}
            size="small"
            style={{ background: 'transparent' }}
          />
        </div>
      ))}

      {/* ════════════════════════════════════════
          3. 公共组件展示
          ════════════════════════════════════════ */}
      <Divider style={DIVIDER_STYLE}>
        <span style={{ fontSize: 15, color: 'var(--text-secondary)' }}>
          03 · 公共组件
        </span>
      </Divider>

      {/* —— StatCard —— */}
      <div style={{ marginBottom: SECTION_GAP }}>
        <Title level={3} style={{ fontSize: 20, color: 'var(--text-primary)', marginBottom: 8, fontWeight: 600 }}>
          StatCard — 统计卡片
        </Title>
        <Text style={{ fontSize: 13, color: 'var(--text-tertiary)', display: 'block', marginBottom: 20 }}>
          封装 Ant Design Card + Statistic，用于展示关键指标数据。
        </Text>

        {/* 演示实例 */}
        <div style={{ marginBottom: 24 }}>
          <Text style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
            <Tag color="geekblue" style={{ border: 'none', fontSize: 11 }}>示例</Tag> 三种不同参数组合
          </Text>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <StatCard
                title="总创作数"
                value={128}
                suffix="篇"
                icon={<AppstoreOutlined />}
                color="#6366f1"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <StatCard
                title="互动率"
                value={87.5}
                suffix="%"
                icon={<HeartOutlined />}
                color="#E8D28A"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <StatCard
                title="活跃用户"
                value={3204}
                icon={<TeamOutlined />}
                color="#A8C7D8"
              />
            </Col>
          </Row>
        </div>

        {/* Props 表 */}
        <Text style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
          <Tag color="purple" style={{ border: 'none', fontSize: 11 }}>Props</Tag>
        </Text>
        <Table
          columns={propColumns}
          dataSource={statCardProps.map((r, i) => ({ ...r, key: i }))}
          pagination={false}
          size="small"
        />
      </div>

      {/* —— EmptyState —— */}
      <div style={{ marginBottom: SECTION_GAP }}>
        <Title level={3} style={{ fontSize: 20, color: 'var(--text-primary)', marginBottom: 8, fontWeight: 600 }}>
          EmptyState — 空状态
        </Title>
        <Text style={{ fontSize: 13, color: 'var(--text-tertiary)', display: 'block', marginBottom: 20 }}>
          封装 Ant Design Empty，统一空状态视觉风格。
        </Text>

        <div style={{ marginBottom: 24 }}>
          <Text style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
            <Tag color="geekblue" style={{ border: 'none', fontSize: 11 }}>示例</Tag>
          </Text>
          <Card bordered={false} style={{ background: 'var(--glass-sm)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
            <EmptyState
              description="没有找到相关内容，换个关键词试试"
              action={<Button type="primary" ghost>返回首页</Button>}
            />
          </Card>
        </div>

        <Text style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
          <Tag color="purple" style={{ border: 'none', fontSize: 11 }}>Props</Tag>
        </Text>
        <Table
          columns={propColumns}
          dataSource={emptyStateProps.map((r, i) => ({ ...r, key: i }))}
          pagination={false}
          size="small"
        />
      </div>

      {/* —— PageHeader —— */}
      <div style={{ marginBottom: SECTION_GAP }}>
        <Title level={3} style={{ fontSize: 20, color: 'var(--text-primary)', marginBottom: 8, fontWeight: 600 }}>
          PageHeader — 页面标题栏
        </Title>
        <Text style={{ fontSize: 13, color: 'var(--text-tertiary)', display: 'block', marginBottom: 20 }}>
          页面统一标题栏，左侧标题 + 描述，右侧操作区。
        </Text>

        <div style={{ marginBottom: 24 }}>
          <Text style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
            <Tag color="geekblue" style={{ border: 'none', fontSize: 11 }}>示例</Tag> 带操作按钮
          </Text>
          <Card bordered={false} style={{ background: 'var(--glass-sm)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
            <PageHeader
              title="创作中心"
              description="管理你的所有内容创作，支持 AI 辅助生成"
              actions={
                <Space>
                  <Button icon={<SearchOutlined />}>搜索</Button>
                  <Button type="primary" icon={<PlusOutlined />}>新建</Button>
                </Space>
              }
            />
          </Card>
        </div>

        <Text style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
          <Tag color="purple" style={{ border: 'none', fontSize: 11 }}>Props</Tag>
        </Text>
        <Table
          columns={propColumns}
          dataSource={pageHeaderProps.map((r, i) => ({ ...r, key: i }))}
          pagination={false}
          size="small"
        />
      </div>

      {/* —— KnowledgeCard —— */}
      <div style={{ marginBottom: SECTION_GAP }}>
        <Title level={3} style={{ fontSize: 20, color: 'var(--text-primary)', marginBottom: 8, fontWeight: 600 }}>
          KnowledgeCard — 知识卡片
        </Title>
        <Text style={{ fontSize: 13, color: 'var(--text-tertiary)', display: 'block', marginBottom: 20 }}>
          知识库文档卡片：标题 + 类型Tag + 国家Tag + 简介 + 时间，支持编辑/删除操作。
        </Text>

        <div style={{ marginBottom: 24 }}>
          <Text style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
            <Tag color="geekblue" style={{ border: 'none', fontSize: 11 }}>示例</Tag> 知识卡片样式
          </Text>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <KnowledgeCard document={mockDoc} />
            </Col>
          </Row>
        </div>

        <Text style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>
          <Tag color="purple" style={{ border: 'none', fontSize: 11 }}>Props</Tag>
        </Text>
        <Table
          columns={propColumns}
          dataSource={knowledgeCardProps.map((r, i) => ({ ...r, key: i }))}
          pagination={false}
          size="small"
        />
      </div>

      {/* ════════════════════════════════════════
          4. 图标库
          ════════════════════════════════════════ */}
      <Divider style={DIVIDER_STYLE}>
        <span style={{ fontSize: 15, color: 'var(--text-secondary)' }}>
          04 · 图标库
        </span>
      </Divider>

      <Text style={{ fontSize: 13, color: 'var(--text-tertiary)', display: 'block', marginBottom: 20 }}>
        项目使用的 Ant Design Outlined 图标集，共 36 个常用图标。统一使用 14px 尺寸，0.75 opacity 融入夜间主题。
      </Text>

      <Row gutter={[12, 12]}>
        {iconList.map((item) => (
          <Col key={item.name} xs={8} sm={6} md={4} lg={3}>
            <Card
              bordered={false}
              hoverable
              style={{
                background: 'var(--glass-sm)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-sm)',
                textAlign: 'center',
                transition: 'all 200ms var(--ease)',
              }}
              styles={{ body: { padding: '18px 8px' } }}
            >
              <div style={{ fontSize: 24, color: 'var(--accent)', marginBottom: 8 }}>
                {item.icon}
              </div>
              <div style={{
                fontSize: 10,
                color: 'var(--text-tertiary)',
                fontFamily: 'var(--font-mono)',
                lineHeight: 1.3,
                wordBreak: 'break-all',
              }}>
                {item.name.replace('Outlined', '')}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ════════════════════════════════════════
          5. 布局规范
          ════════════════════════════════════════ */}
      <Divider style={DIVIDER_STYLE}>
        <span style={{ fontSize: 15, color: 'var(--text-secondary)' }}>
          05 · 布局规范
        </span>
      </Divider>

      <Text style={{ fontSize: 13, color: 'var(--text-tertiary)', display: 'block', marginBottom: 24 }}>
        项目三种典型布局模式，对应不同使用场景。
      </Text>

      <Row gutter={[20, 20]}>
        {/* MainLayout */}
        <Col xs={24} md={8}>
          <Card
            bordered={false}
            title={<span style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600 }}>MainLayout</span>}
            style={{ background: 'var(--glass-sm)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', height: '100%' }}
            styles={{
              header: { borderBottom: '1px solid var(--border-subtle)' },
              body: { padding: 16 },
            }}
          >
            {/* 简化示意图 */}
            <div style={{
              background: 'var(--night-900)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              lineHeight: 1.6,
            }}>
              <div style={{ color: '#E8D28A' }}>{'<Layout>'}</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 12 }}>{'<Header sticky>'}</div>
              <div style={{ color: 'var(--text-tertiary)', paddingLeft: 24 }}>Logo + Nav(横排) + User</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 12, marginTop: 4 }}>{'<Content>'}</div>
              <div style={{ color: 'var(--text-tertiary)', paddingLeft: 24 }}>{'<Outlet /></' + 'div>'}</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 12 }}>{'</Content>'}</div>
              <div style={{ color: '#E8D28A' }}>{'</Layout>'}</div>
              <div style={{ color: 'var(--text-muted)', paddingLeft: 12, marginTop: 6 }}>
                + FireflyCanvas(全局氛围)
              </div>
            </div>
            <Descriptions column={1} size="small" labelStyle={{ color: 'var(--text-tertiary)', fontSize: 12 }} contentStyle={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              <Descriptions.Item label="场景">主应用布局</Descriptions.Item>
              <Descriptions.Item label="结构">顶部导航 + 内容区</Descriptions.Item>
              <Descriptions.Item label="导航">横向19项入口</Descriptions.Item>
              <Descriptions.Item label="特色">萤火虫粒子背景</Descriptions.Item>
              <Descriptions.Item label="使用">绝大多数页面</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* AppLayout */}
        <Col xs={24} md={8}>
          <Card
            bordered={false}
            title={<span style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600 }}>AppLayout</span>}
            style={{ background: 'var(--glass-sm)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', height: '100%' }}
            styles={{
              header: { borderBottom: '1px solid var(--border-subtle)' },
              body: { padding: 16 },
            }}
          >
            <div style={{
              background: 'var(--night-900)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              lineHeight: 1.6,
            }}>
              <div style={{ color: '#E8D28A' }}>{'<Layout>'}</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 12 }}>{'<Sider collapsible>'}</div>
              <div style={{ color: 'var(--text-tertiary)', paddingLeft: 24 }}>Logo + Menu(8项)</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 12, marginTop: 4 }}>{'<Layout>'}</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 24 }}>{'<Header>'}</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 24 }}>{'<Content card>'}</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 12 }}>{'</Layout>'}</div>
              <div style={{ color: '#E8D28A' }}>{'</Layout>'}</div>
            </div>
            <Descriptions column={1} size="small" labelStyle={{ color: 'var(--text-tertiary)', fontSize: 12 }} contentStyle={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              <Descriptions.Item label="场景">Postiz 旧版布局</Descriptions.Item>
              <Descriptions.Item label="结构">左侧边栏 + Header + 内容</Descriptions.Item>
              <Descriptions.Item label="导航">左侧纵向Menu</Descriptions.Item>
              <Descriptions.Item label="特色">可折叠Sider</Descriptions.Item>
              <Descriptions.Item label="使用">发布/分析/媒体等</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* AuthLayout */}
        <Col xs={24} md={8}>
          <Card
            bordered={false}
            title={<span style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 600 }}>AuthLayout</span>}
            style={{ background: 'var(--glass-sm)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', height: '100%' }}
            styles={{
              header: { borderBottom: '1px solid var(--border-subtle)' },
              body: { padding: 16 },
            }}
          >
            <div style={{
              background: 'var(--night-900)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              lineHeight: 1.6,
            }}>
              <div style={{ color: '#E8D28A' }}>{'<Layout>'}</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 12 }}>{'<Content centered>'}</div>
              <div style={{ color: 'var(--text-tertiary)', paddingLeft: 24 }}>{'<Card maxW=440>'}</div>
              <div style={{ color: 'var(--text-muted)', paddingLeft: 36 }}>Logo + 品牌文案</div>
              <div style={{ color: 'var(--text-muted)', paddingLeft: 36 }}>{'<Outlet />'}</div>
              <div style={{ color: 'var(--text-tertiary)', paddingLeft: 24 }}>{'</Card>'}</div>
              <div style={{ color: '#A8C7D8', paddingLeft: 12 }}>{'</Content>'}</div>
              <div style={{ color: '#E8D28A' }}>{'</Layout>'}</div>
            </div>
            <Descriptions column={1} size="small" labelStyle={{ color: 'var(--text-tertiary)', fontSize: 12 }} contentStyle={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              <Descriptions.Item label="场景">认证页面布局</Descriptions.Item>
              <Descriptions.Item label="结构">居中单卡片</Descriptions.Item>
              <Descriptions.Item label="导航">无导航</Descriptions.Item>
              <Descriptions.Item label="特色">品牌展示 + 简洁</Descriptions.Item>
              <Descriptions.Item label="使用">登录/注册/忘记密码</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* 底部标注 */}
      <div style={{ marginTop: SECTION_GAP, textAlign: 'center' }}>
        <Text style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          萤火秘境 · Quiet · Mystical · Warm · Precise · Immersive
        </Text>
      </div>
    </div>
  );
};

export default DesignSystem;
